import React, { useEffect, useState, useRef, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import { useSnackbar } from 'react-simple-snackbar'
import * as api from '../../api/index'
import { isAdmin } from '../../utils/auth'
import styles from './Dashboard.module.css'
import Spinner from '../Spinner/Spinner'
import ConfirmModal from '../ConfirmModal/ConfirmModal'

const MONTHS = [
  '', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
]

const formatTND = (n) =>
  Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })

const formatPeriod = (period) => {
  if (!period) return '—'
  const [year, month] = period.split('-')
  return `${MONTHS[Number(month)]} ${year}`
}

const Dashboard = () => {
  const history = useHistory()
  const profile = JSON.parse(localStorage.getItem('profile'))
  const token = profile?.token
  const now = new Date()

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [pageLoading, setPageLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1)
  const [selectedYear, setSelectedYear] = useState(now.getFullYear())
  const [selectedIds, setSelectedIds] = useState([])
  const [deleting, setDeleting] = useState(false)
  const [confirmModal, setConfirmModal] = useState(null)
  const hasLoadedOnce = useRef(false)
  const limit = 5
  const [openSnackbar] = useSnackbar()
  const openSnackbarRef = useRef(openSnackbar)
  openSnackbarRef.current = openSnackbar
  const admin = isAdmin()

  const loadDashboard = useCallback(async () => {
    if (hasLoadedOnce.current) setPageLoading(true)
    else setLoading(true)
    try {
      const { data: stats } = await api.fetchRhDashboardStats({
        page,
        limit,
        month: selectedMonth,
        year: selectedYear,
      })
      setData(stats)
      hasLoadedOnce.current = true
    } catch (error) {
      console.error(error)
      openSnackbarRef.current(error?.response?.data?.message || 'Erreur chargement du tableau de bord')
    } finally {
      setLoading(false)
      setPageLoading(false)
    }
  }, [page, selectedMonth, selectedYear])

  useEffect(() => {
    if (!token) {
      history.push('/')
      return
    }
    if (profile?.result?.role === 'corporate') {
      history.push('/corporate/dashboard')
      return
    }
    loadDashboard()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, page, selectedMonth, selectedYear, profile?.result?.role])

  useEffect(() => {
    setSelectedIds([])
  }, [page])

  const handleMonthChange = (month) => {
    setSelectedMonth(Number(month))
  }

  const handleYearChange = (year) => {
    setSelectedYear(Number(year))
  }

  const goToCurrentMonth = () => {
    setSelectedMonth(now.getMonth() + 1)
    setSelectedYear(now.getFullYear())
  }

  const selectPeriodFromCampaign = (period) => {
    const [year, month] = period.split('-')
    setSelectedYear(Number(year))
    setSelectedMonth(Number(month))
  }

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )
  }

  const toggleSelectAllOnPage = (campaigns) => {
    const pageIds = campaigns.map((c) => c.id)
    const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id))
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)))
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...pageIds])])
    }
  }

  const executeDeleteCampaigns = async (ids) => {
    if (!ids.length) return
    setDeleting(true)
    try {
      if (ids.length === 1) {
        await api.deleteCampaign(ids[0])
      } else {
        await api.deleteCampaignsBulk(ids)
      }
      openSnackbar(`${ids.length} campagne(s) supprimée(s)`)
      setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)))
      await loadDashboard()
    } catch (error) {
      openSnackbar(error?.response?.data?.message || 'Erreur lors de la suppression')
    } finally {
      setDeleting(false)
    }
  }

  const requestDeleteCampaigns = (ids) => {
    if (!ids.length) return
    const label = ids.length === 1 ? 'cette campagne' : `${ids.length} campagnes`
    setConfirmModal({
      ids,
      variant: 'warning',
      title: 'Supprimer la campagne',
      message: `Supprimer ${label} ?`,
      detail: 'Toutes les factures associées seront définitivement supprimées.',
      confirmLabel: 'Supprimer',
    })
  }

  const handleConfirmModal = () => {
    if (!confirmModal) return
    const { ids } = confirmModal
    setConfirmModal(null)
    executeDeleteCampaigns(ids)
  }

  if (!token) return null

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner />
      </div>
    )
  }

  if (!data) {
    return (
      <div className={styles.page}>
        <p className={styles.empty}>Impossible de charger le tableau de bord. Réessayez ou reconnectez-vous.</p>
      </div>
    )
  }

  const {
    stats,
    selectedCampaign,
    selectedPeriod,
    isCurrentPeriod,
    currentYear,
    availableYears = [],
    recentCampaigns = [],
    campaignsPagination,
  } = data

  const hasCampaign = stats?.hasCampaign
  const emailStats = stats?.periodEmailStats || {}
  const emailTotal = (emailStats.sent || 0) + (emailStats.pending || 0) + (emailStats.failed || 0)
  const emailProgress = emailTotal > 0 ? Math.round((emailStats.sent / emailTotal) * 100) : 0
  const years = availableYears.length > 0
    ? availableYears
    : [currentYear || now.getFullYear()]

  const pageIds = recentCampaigns?.map((c) => c.id) || []
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id))

  return (
    <div className={styles.page}>
      <div className={styles.topBar}>
        <div>
          <h1>Tableau de bord RH</h1>
          <p>California Gym — Facturation des coaches</p>
        </div>
        <button className={styles.ctaBtn} onClick={() => history.push('/import')}>
          + Nouvel import mensuel
        </button>
      </div>

      <div className={styles.filterBar}>
        <div className={styles.filterGroup}>
          <label htmlFor="dash-month">Mois</label>
          <select
            id="dash-month"
            className={styles.filterSelect}
            value={selectedMonth}
            onChange={(e) => handleMonthChange(e.target.value)}
          >
            {MONTHS.slice(1).map((name, i) => (
              <option key={name} value={i + 1}>{name}</option>
            ))}
          </select>
        </div>
        <div className={styles.filterGroup}>
          <label htmlFor="dash-year">Année</label>
          <select
            id="dash-year"
            className={styles.filterSelect}
            value={selectedYear}
            onChange={(e) => handleYearChange(e.target.value)}
          >
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        {!isCurrentPeriod && (
          <button className={styles.resetBtn} onClick={goToCurrentMonth}>
            Revenir au mois en cours
          </button>
        )}
      </div>

      <div className={styles.periodBanner}>
        <div>
          <span className={styles.periodLabel}>
            {isCurrentPeriod ? 'Période en cours' : 'Période sélectionnée'}
          </span>
          <strong>{formatPeriod(selectedPeriod)}</strong>
        </div>
        <div className={styles.periodStatus}>
          {selectedCampaign ? (
            <>
              <span className={styles.badgeOk}>
                {isCurrentPeriod ? 'Campagne active' : 'Campagne trouvée'}
              </span>
              <span>{selectedCampaign.successCount} factures générées</span>
              <button
                className={styles.linkBtn}
                onClick={() => history.push(`/campagnes/${selectedCampaign._id}`)}
              >
                Voir la campagne →
              </button>
            </>
          ) : (
            <>
              <span className={styles.badgeWarn}>Aucune campagne ce mois</span>
              {isCurrentPeriod ? (
                <button className={styles.linkBtn} onClick={() => history.push('/import')}>
                  Lancer l'import →
                </button>
              ) : (
                <button
                  className={styles.linkBtn}
                  onClick={() => history.push(`/import?month=${selectedMonth}&year=${selectedYear}`)}
                >
                  Importer pour cette période →
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {!hasCampaign ? (
        <div className={styles.emptyPeriod}>
          <h3>Aucune donnée pour {formatPeriod(selectedPeriod)}</h3>
          <p>
            Les statistiques ci-dessous concernent uniquement ce mois.
            Sélectionnez un autre mois ou consultez l'historique en bas de page.
          </p>
        </div>
      ) : null}

      <div className={styles.kpiGrid}>
        <div className={`${styles.kpiCard} ${styles.kpiPrimary}`}>
          <span className={styles.kpiLabel}>Coaches facturés</span>
          <span className={styles.kpiValue}>{stats?.periodInvoices || 0}</span>
          <span className={styles.kpiSub}>{formatPeriod(selectedPeriod)}</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Honoraires</span>
          <span className={styles.kpiValue}>{formatTND(stats?.periodHonoraires)} TND</span>
          <span className={styles.kpiSub}>{formatPeriod(selectedPeriod)}</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Net à payer</span>
          <span className={styles.kpiValue}>{formatTND(stats?.periodNet)} TND</span>
          <span className={styles.kpiSub}>{formatPeriod(selectedPeriod)}</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Campagne ce mois</span>
          <span className={styles.kpiValue}>{hasCampaign ? 'Oui' : 'Non'}</span>
          <span className={styles.kpiSub}>{formatPeriod(selectedPeriod)}</span>
        </div>
      </div>

      <div className={styles.twoCol}>
        <div className={styles.panel}>
          <h2>Envoi des emails — {formatPeriod(selectedPeriod)}</h2>
          {hasCampaign ? (
            <>
              <div className={styles.emailStats}>
                <div className={styles.emailRow}>
                  <span className={styles.dotSent} /> Envoyées
                  <strong>{emailStats.sent || 0}</strong>
                </div>
                <div className={styles.emailRow}>
                  <span className={styles.dotPending} /> En attente
                  <strong>{emailStats.pending || 0}</strong>
                </div>
                <div className={styles.emailRow}>
                  <span className={styles.dotFailed} /> Échecs
                  <strong>{emailStats.failed || 0}</strong>
                </div>
              </div>
              <div className={styles.progressWrap}>
                <div className={styles.progressBar} style={{ width: `${emailProgress}%` }} />
              </div>
              <p className={styles.progressText}>{emailProgress}% des factures envoyées par email</p>
            </>
          ) : (
            <p className={styles.panelEmpty}>Aucun envoi — pas de campagne pour cette période.</p>
          )}
        </div>

        <div className={styles.panel}>
          <h2>Résumé — {formatPeriod(selectedPeriod)}</h2>
          <ul className={styles.summaryList}>
            <li>
              <span>Campagne</span>
              <strong>{hasCampaign ? 'Existe' : 'Aucune'}</strong>
            </li>
            <li>
              <span>Factures générées</span>
              <strong>{stats?.periodInvoices || 0}</strong>
            </li>
            <li>
              <span>Honoraires</span>
              <strong>{formatTND(stats?.periodHonoraires)} TND</strong>
            </li>
            <li>
              <span>Net à payer</span>
              <strong>{formatTND(stats?.periodNet)} TND</strong>
            </li>
            <li>
              <span>Client facturé</span>
              <strong>Société Sport et Loisirs</strong>
            </li>
          </ul>
        </div>
      </div>

      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <div>
            <h2>Historique des campagnes</h2>
            <p className={styles.historyHint}>
              Toutes les campagnes enregistrées. Cliquez sur une ligne pour afficher ses statistiques.
              {admin && ' En tant qu\'administrateur, vous pouvez supprimer une ou plusieurs campagnes.'}
            </p>
          </div>
          <div className={styles.panelActions}>
            {admin && selectedIds.length > 0 && (
              <button
                type="button"
                className={styles.deleteBtn}
                disabled={deleting || pageLoading}
                onClick={() => requestDeleteCampaigns(selectedIds)}
              >
                {deleting ? 'Suppression...' : `Supprimer (${selectedIds.length})`}
              </button>
            )}
            {campaignsPagination?.total > 0 && (
              <span className={styles.pageInfo}>
                {campaignsPagination.total} campagne(s) · page {campaignsPagination.page}/{campaignsPagination.totalPages}
              </span>
            )}
          </div>
        </div>
        {recentCampaigns.length === 0 ? (
          <p className={styles.empty}>Aucune campagne enregistrée. Commencez par un import mensuel.</p>
        ) : (
          <div className={pageLoading ? styles.tableLoading : undefined}>
            <table className={styles.table}>
              <thead>
                <tr>
                  {admin && (
                    <th className={styles.checkCol}>
                      <input
                        type="checkbox"
                        checked={allPageSelected}
                        onChange={() => toggleSelectAllOnPage(recentCampaigns)}
                        aria-label="Sélectionner toutes les campagnes de la page"
                      />
                    </th>
                  )}
                  <th>Période</th>
                  <th>Factures</th>
                  <th>Fichier source</th>
                  <th>Statut</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {recentCampaigns.map((c) => (
                  <tr
                    key={c.id}
                    className={c.period === selectedPeriod ? styles.rowActive : undefined}
                  >
                    {admin && (
                      <td className={styles.checkCol}>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(c.id)}
                          onChange={() => toggleSelect(c.id)}
                          aria-label={`Sélectionner ${formatPeriod(c.period)}`}
                        />
                      </td>
                    )}
                    <td>
                      <button
                        type="button"
                        className={styles.periodLink}
                        onClick={() => selectPeriodFromCampaign(c.period)}
                      >
                        {formatPeriod(c.period)}
                      </button>
                    </td>
                    <td>{c.invoiceCount}</td>
                    <td>{c.fileName}</td>
                    <td>
                      <span className={c.status === 'completed' ? styles.badgeOk : styles.badgeWarn}>
                        {c.status === 'completed' ? 'Terminée' : c.status}
                      </span>
                    </td>
                    <td>
                      <div className={styles.actionCell}>
                        <button
                          type="button"
                          className={styles.tableBtn}
                          onClick={() => history.push(`/campagnes/${c.id}`)}
                        >
                          Ouvrir
                        </button>
                        {admin && (
                          <button
                            type="button"
                            className={styles.deleteBtnSmall}
                            disabled={deleting}
                            onClick={() => requestDeleteCampaigns([c.id])}
                          >
                            Supprimer
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {campaignsPagination?.totalPages > 1 && (
              <div className={styles.pagination}>
                <button
                  className={styles.pageBtn}
                  disabled={page <= 1 || pageLoading}
                  onClick={() => setPage((p) => p - 1)}
                >
                  ← Précédent
                </button>
                <span className={styles.pageLabel}>
                  Page {campaignsPagination.page} sur {campaignsPagination.totalPages}
                </span>
                <button
                  className={styles.pageBtn}
                  disabled={page >= campaignsPagination.totalPages || pageLoading}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Suivant →
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <ConfirmModal
        open={!!confirmModal}
        variant={confirmModal?.variant || 'warning'}
        title={confirmModal?.title}
        message={confirmModal?.message}
        detail={confirmModal?.detail}
        confirmLabel={confirmModal?.confirmLabel || 'Supprimer'}
        onConfirm={handleConfirmModal}
        onCancel={() => setConfirmModal(null)}
        loading={deleting}
      />
    </div>
  )
}

export default Dashboard
